import { useState, useEffect } from 'react';
import { useLang } from '../lib/lang';
import { useBranding } from '../lib/branding';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { ShieldCheck, Sparkles } from 'lucide-react';

const LOGO_URL_DEFAULT = '/assets/winluxor-logo.png';

export default function AgeVerification() {
  const { t } = useLang();
  const branding = useBranding();
  const LOGO_URL = branding.logo_main_url || LOGO_URL_DEFAULT;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Skip age verification for technical/admin paths so they're always usable
    const path = window.location.pathname || '';
    if (path.startsWith('/admin') || path === '/diagnostic') {
      return;
    }
    const verified = localStorage.getItem('winluxor_age_verified');
    if (!verified) {
      setOpen(true);
    }
  }, []);

  const handleConfirm = () => {
    localStorage.setItem('winluxor_age_verified', 'true');
    setOpen(false);
  };

  const handleDeny = () => {
    window.location.href = 'https://www.google.com';
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-lg p-0 overflow-hidden border-0 [&>button]:hidden bg-transparent shadow-none"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Outer green frame */}
        <div className="relative bg-gradient-to-b from-[#0E4936] via-[#0B3D2E] to-[#08291F] rounded-2xl border-2 border-[hsl(var(--gold)/0.45)] shadow-[0_30px_80px_rgba(0,0,0,0.6),0_0_0_1px_rgba(201,169,97,0.15)]">
          {/* Gold corner accents */}
          <span className="absolute top-3 left-3 w-5 h-5 border-t-2 border-l-2 border-gold rounded-tl-md pointer-events-none" />
          <span className="absolute top-3 right-3 w-5 h-5 border-t-2 border-r-2 border-gold rounded-tr-md pointer-events-none" />
          <span className="absolute bottom-3 left-3 w-5 h-5 border-b-2 border-l-2 border-gold rounded-bl-md pointer-events-none" />
          <span className="absolute bottom-3 right-3 w-5 h-5 border-b-2 border-r-2 border-gold rounded-br-md pointer-events-none" />

          {/* Subtle spotlight */}
          <div className="absolute inset-0 hero-spotlight pointer-events-none rounded-2xl" />
          <div className="absolute inset-0 noise-overlay opacity-50 pointer-events-none rounded-2xl" />

          <div className="relative px-7 sm:px-10 py-9 sm:py-11 flex flex-col items-center text-center" style={{ color: 'hsl(43 38% 92%)' }}>
            {/* Logo XXL */}
            <img
              src={LOGO_URL}
              alt="WINLUXOR"
              className="h-32 sm:h-40 w-auto rounded-lg mb-5 shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
            />

            <div className="divider-gold-thick w-32 mb-5" />

            <p className="gold-line mb-5">
              <Sparkles className="w-3 h-3" />
              {t('ageGate18Plus') || 'Age Verification'}
            </p>

            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-3 leading-tight" style={{ color: 'hsl(43 38% 92%)' }}>
              {t('ageVerificationTitle')}
            </h2>

            <p className="text-sm sm:text-base leading-relaxed max-w-md mb-8 font-light" style={{ color: 'hsl(43 28% 78%)' }}>
              {t('ageVerificationDesc')}
            </p>

            <div className="flex items-center gap-2 mb-7 px-4 py-2 rounded-full bg-[hsl(var(--gold)/0.10)] border border-[hsl(var(--gold)/0.30)]">
              <ShieldCheck className="w-4 h-4 text-gold" />
              <span className="text-xs text-gold font-semibold tracking-[0.12em] uppercase">UK Licensed Operator</span>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 w-full">
              <Button
                onClick={handleConfirm}
                className="flex-1 btn-gold btn-press rounded-xl h-14 text-sm font-bold tracking-[0.12em] uppercase"
                data-testid="age-verify-yes"
              >
                {t('ageYes')}
              </Button>
              <Button
                onClick={handleDeny}
                variant="outline"
                style={{ color: 'hsl(43 38% 92%)' }}
                className="flex-1 rounded-xl h-14 text-sm font-semibold tracking-[0.12em] uppercase border-2 border-[hsl(var(--gold)/0.55)] bg-transparent hover:bg-[hsl(var(--gold)/0.10)] hover:!text-gold hover:border-gold"
                data-testid="age-verify-no"
              >
                {t('ageNo')}
              </Button>
            </div>

            <p className="text-[10px] mt-6 leading-relaxed max-w-sm tracking-wide" style={{ color: 'hsl(43 18% 62%)' }}>
              {t('ageDisclaimer')}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
